import { Fragment, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
// Filter / date / pagination controls use the ui kit; the TABLE itself is hand-built with Tailwind
// (design tokens from styles/theme.css → theme-aware + dark-mode correct).
import { Input, Select, Segmented, Button, Tooltip, DatePicker, Pagination } from '../../ui';
import type { Dayjs } from 'dayjs';
import { warehouseText } from '../../lib/warehouseLabel';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { fmtPacks, fmtUom } from '../../lib/format';

interface Stock {
  warehouses: Record<string, number>;
  total: number;
}

interface Variant {
  productId: string;
  sku: string;
  groupCode?: string | null;
  variantLabel?: string | null;
  name: string;
  mrp: number;
  price: number;
  uom: string;
  packSize: number;
  moq: number;
  imageUrl?: string | null;
  stock: Stock;
}

interface Group {
  key: string;
  groupName: string;
  imageUrl?: string | null;
  variants: Variant[];
  total: number;
  warehouseTotals: Record<string, number>;
}

function buildGroups(items: Variant[]): Group[] {
  const byKey = new Map<string, Group>();
  for (const it of items) {
    const key = it.groupCode ?? `__${it.sku}`;
    let g = byKey.get(key);
    if (!g) {
      const groupName = it.variantLabel
        ? it.name.replace(new RegExp(`\\s*${it.variantLabel}\\s*$`, 'i'), '').trim()
        : it.name;
      g = {
        key,
        groupName: groupName || it.name,
        imageUrl: it.imageUrl,
        variants: [],
        total: 0,
        warehouseTotals: {},
      };
      byKey.set(key, g);
    }
    g.variants.push(it);
    g.total += it.stock.total;
    for (const [wh, qty] of Object.entries(it.stock.warehouses ?? {})) {
      g.warehouseTotals[wh] = (g.warehouseTotals[wh] ?? 0) + qty;
    }
  }
  for (const g of byKey.values()) g.variants.sort((a, b) => a.packSize - b.packSize);
  return Array.from(byKey.values()).sort((a, b) => a.groupName.localeCompare(b.groupName));
}

const fmt = (n: number) => n.toLocaleString('en-IN');
const inr = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const fmtDate = (d?: string | null) => {
  if (!d) return '—';
  const [y, m, dd] = String(d).slice(0, 10).split('-');
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m) - 1];
  return mon ? `${dd} ${mon} ${y}` : String(d);
};
const daysUntil = (d?: string | null): number | null => {
  if (!d) return null;
  const t = new Date(String(d).slice(0, 10) + 'T00:00:00').getTime();
  return Math.ceil((t - new Date().setHours(0, 0, 0, 0)) / 86400000);
};

// ── Tailwind presentational atoms (replace Antd Tag / Avatar / Empty / Skeleton) ────────────────
const PILL: Record<string, string> = {
  default: 'bg-surface-3 text-muted border-line',
  green: 'bg-brand-500/12 text-brand-700 dark:text-brand-300 border-brand-500/25',
  blue: 'bg-sky-500/12 text-sky-600 dark:text-sky-400 border-sky-500/25',
  orange: 'bg-warning-500/15 text-warning-600 dark:text-harvest-300 border-warning-500/30',
  red: 'bg-danger-500/12 text-danger-600 dark:text-danger-500 border-danger-500/30',
  gold: 'bg-harvest-400/18 text-harvest-700 dark:text-harvest-300 border-harvest-400/35',
};
const STATUS_PILL: Record<string, string> = { AVAILABLE: 'green', QUARANTINE: 'orange', EXPIRED: 'red', DAMAGED: 'red', BLOCKED: 'red', MIXED: 'gold' };

function Pill({ color = 'default', className = '', children }: { color?: string; className?: string; children: ReactNode }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-pill border px-2 py-px text-xs font-medium leading-5 ${PILL[color] ?? PILL.default} ${className}`}>
      {children}
    </span>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
      className={`h-3.5 w-3.5 shrink-0 text-subtle transition-transform duration-150 ${open ? 'rotate-90' : ''}`}>
      <path d="M6 4l4 4-4 4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function GroupAvatar({ url, name }: { url?: string | null; name: string }) {
  return (
    <div className="grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-control bg-brand-600 text-base font-semibold text-white">
      {url ? <img src={url} alt="" className="h-full w-full object-cover" /> : (name?.[0]?.toUpperCase() ?? '?')}
    </div>
  );
}

const EmptyState = ({ text }: { text: string }) => (
  <div className="px-4 py-12 text-center text-sm text-subtle">{text}</div>
);

const TH = 'px-3 py-2.5 text-left text-2xs font-semibold uppercase tracking-wide text-muted';

interface Batch {
  batchId: string; warehouse: string; batchNo: string; sourceBatchNo?: string | null;
  mfgDate?: string | null; expDate?: string | null; status: string;
  qty: number; unitCost?: number; value?: number;
}

interface AggBatch {
  batchNo: string; sourceBatchNo?: string | null; mfgDate?: string | null; expDate?: string | null;
  qty: number; value: number; status: string; statuses: Set<string>; splits: Batch[];
}

// Shared cell renderers (Tailwind).
const expiryCell = (v?: string | null) => {
  const d = daysUntil(v);
  return (
    <span className="whitespace-nowrap">
      {fmtDate(v)}
      {d != null && <Pill className="ml-1.5" color={d < 0 ? 'red' : d < 90 ? 'orange' : 'default'}>{d < 0 ? 'expired' : `${d}d left`}</Pill>}
    </span>
  );
};
const statusTag = (v: string) => v === 'MIXED'
  ? <Tooltip label="Status differs across warehouses — expand to see"><span className="inline-flex"><Pill color="gold">Mixed</Pill></span></Tooltip>
  : <Pill color={STATUS_PILL[v] ?? 'default'}>{v}</Pill>;

// "Expired" = status EXPIRED **or** past the expiry date. A batch marked AVAILABLE but past its expiry isn't
// auto-flipped to EXPIRED by any job, yet it's not sellable (backend excludes it from stock/valuation), so we
// surface it as EXPIRED here too — keeping the status column honest with the (already-excluded) totals.
const effStatus = (b: { status: string; expDate?: string | null }) => {
  const d = daysUntil(b.expDate);
  return b.status === 'AVAILABLE' && d != null && d < 0 ? 'EXPIRED' : b.status;
};

// Per-product batch drill-down. One row per BATCH (totals rolled up across warehouses); expand a row
// to see the per-warehouse split. Sorted first-expiry-first-out, with batch search + status/expiry
// filters and a summary strip so the lots that need attention surface even in a long list.
function BatchPanel({ productId, hideWarehouse, warehouseCode }: { productId: string; hideWarehouse?: boolean; warehouseCode?: string }) {
  const { data: batches, isLoading } = useQuery({
    queryKey: ['product-batches', productId],
    queryFn: () => request<Batch[]>({ url: `/catalog/product-batches/${productId}` }),
  });
  const [bStatus, setBStatus] = useState<string>();
  const [bExp, setBExp] = useState<'any' | 'exp' | '30' | '90'>('any');
  const [bSearch, setBSearch] = useState('');
  const [mfgRange, setMfgRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [expRange, setExpRange] = useState<[Dayjs | null, Dayjs | null]>([null, null]);
  const [bPage, setBPage] = useState(1);
  const [bPageSize, setBPageSize] = useState(8);
  const [openBatches, setOpenBatches] = useState<Set<string>>(new Set());
  const toggleBatch = (bn: string) => setOpenBatches((s) => { const n = new Set(s); n.has(bn) ? n.delete(bn) : n.add(bn); return n; });

  if (isLoading) {
    return (
      <div className="my-3 space-y-2">
        <div className="h-4 w-1/3 animate-pulse rounded bg-surface-3" />
        <div className="h-4 w-2/3 animate-pulse rounded bg-surface-3" />
      </div>
    );
  }
  if (!batches?.length) return <EmptyState text="No batches in stock" />;
  // This is the SELLABLE stock view: expired / QC-hold / blocked / past-expiry lots live in the Non-sellable
  // tab (and are already excluded from the totals above), so drop them here to keep the batch list consistent
  // with the totals. effStatus === 'AVAILABLE' means AVAILABLE **and** in-date.
  const sellableBatches = batches.filter((b) => effStatus(b) === 'AVAILABLE');
  const hiddenNonSellable = new Set(batches.filter((b) => effStatus(b) !== 'AVAILABLE').map((b) => b.batchNo)).size;
  if (!sellableBatches.length) return <EmptyState text={`No sellable batches — ${hiddenNonSellable} non-sellable lot${hiddenNonSellable === 1 ? '' : 's'} hidden (see the Non-sellable tab)`} />;
  const hasValue = sellableBatches.some((b) => b.value != null);
  // Only one warehouse is ever in play in a scoped page (warehouseCode) or a single-warehouse role
  // (hideWarehouse) — there's nothing to expand into, so render flat batch rows.
  const hideWh = hideWarehouse || !!warehouseCode;

  // Warehouse scope, then collapse to ONE row per batch number; the per-warehouse rows become the
  // expandable children. Status rolls up to "Mixed" when it differs across warehouses.
  const scoped = warehouseCode
    ? sellableBatches.filter((b) => b.warehouse === warehouseCode || (b.warehouse ?? '').includes(`(${warehouseCode})`))
    : sellableBatches;
  const byBatch = new Map<string, AggBatch>();
  for (const b of scoped) {
    let g = byBatch.get(b.batchNo);
    if (!g) { g = { batchNo: b.batchNo, sourceBatchNo: b.sourceBatchNo ?? null, mfgDate: b.mfgDate, expDate: b.expDate, qty: 0, value: 0, status: '', statuses: new Set(), splits: [] }; byBatch.set(b.batchNo, g); }
    if (!g.sourceBatchNo && b.sourceBatchNo) g.sourceBatchNo = b.sourceBatchNo;
    g.qty += Number(b.qty) || 0;
    g.value += Number(b.value) || 0;
    g.statuses.add(effStatus(b));
    g.splits.push(b);
  }
  const all = Array.from(byBatch.values()).map((g) => ({
    ...g,
    status: g.statuses.size === 1 ? [...g.statuses][0] : 'MIXED',
    splits: [...g.splits].sort((a, b) => (a.warehouse || '').localeCompare(b.warehouse || '')),
  }));
  // FEFO — soonest expiry first; blank expiry sinks to the bottom.
  const expTs = (d?: string | null) => (d ? new Date(String(d).slice(0, 10) + 'T00:00:00').getTime() : Number.POSITIVE_INFINITY);
  all.sort((a, b) => expTs(a.expDate) - expTs(b.expDate));

  // Overview counts (over the warehouse-scoped set, before the in-panel filters) so the strip is stable.
  // "Expired" = status EXPIRED OR date-passed (via effStatus in `statuses`) — so a marked-EXPIRED lot with no
  // date (e.g. a returned RET-EXPIRED lot) is counted too, not just date-passed ones.
  const expired = all.filter((g) => g.statuses.has('EXPIRED')).length;
  const expiring = all.filter((g) => { const d = daysUntil(g.expDate); return d != null && d >= 0 && d <= 90; }).length;

  // In-panel filters: status (matches any warehouse's status on the batch), expiry bucket, batch-no search.
  let rows = all;
  if (bStatus) rows = rows.filter((g) => g.statuses.has(bStatus));
  if (bExp !== 'any') rows = rows.filter((g) => { if (bExp === 'exp') return g.statuses.has('EXPIRED'); const d = daysUntil(g.expDate); if (d == null) return false; if (bExp === '30') return d >= 0 && d <= 30; return d >= 0 && d <= 90; });
  const q = bSearch.trim().toLowerCase();
  // Match the internal batch OR the original printed batch, so searching the printed number finds RET- lots.
  if (q) rows = rows.filter((g) => g.batchNo.toLowerCase().includes(q) || (g.sourceBatchNo ?? '').toLowerCase().includes(q));
  // Date-window filters on the batch's mfg / expiry date (YYYY-MM-DD strings compare chronologically).
  const inRange = (val: string | null | undefined, r: [Dayjs | null, Dayjs | null]) => {
    if (!r[0] && !r[1]) return true;
    if (!val) return false;
    const d = String(val).slice(0, 10);
    if (r[0] && d < r[0].format('YYYY-MM-DD')) return false;
    if (r[1] && d > r[1].format('YYYY-MM-DD')) return false;
    return true;
  };
  if (mfgRange[0] || mfgRange[1]) rows = rows.filter((g) => inRange(g.mfgDate, mfgRange));
  if (expRange[0] || expRange[1]) rows = rows.filter((g) => inRange(g.expDate, expRange));

  const statuses = Array.from(new Set(sellableBatches.map((b) => effStatus(b)))).sort();
  const showTools = all.length > 1;                       // batch filter bar whenever there's >1 batch
  const showStrip = all.length > 1 && (expired > 0 || expiring > 0);
  const batchFiltersOn = !!(bSearch || bStatus || bExp !== 'any' || mfgRange[0] || mfgRange[1] || expRange[0] || expRange[1]);
  const resetBatchFilters = () => { setBSearch(''); setBStatus(undefined); setBExp('any'); setMfgRange([null, null]); setExpRange([null, null]); };
  // Paginate the batch list ourselves so the control can live up in the filter bar. Clamp the page so
  // narrowing the filters never strands you on an empty page.
  const bPageCount = Math.max(1, Math.ceil(rows.length / bPageSize));
  const bCurrent = Math.min(bPage, bPageCount);
  const pagedRows = rows.slice((bCurrent - 1) * bPageSize, bCurrent * bPageSize);

  return (
    <div className="my-3.5">
      {(showStrip || showTools) && (
        <div className="mb-4 mt-1 rounded-control border border-line bg-surface-2 px-3.5 py-3">
          {showStrip && (
            <div className={`flex flex-wrap items-center gap-1.5 text-xs ${showTools ? 'mb-2' : ''}`}>
              <span className="text-muted">{all.length} batches</span>
              {expiring > 0 && <Pill color="orange">{expiring} expiring ≤90d</Pill>}
              {expired > 0 && <Pill color="red">{expired} expired</Pill>}
              <span className="text-subtle">· soonest expiry first</span>
            </div>
          )}
          {showTools && (
            <div className="flex flex-wrap items-center gap-2">
              <Input allowClear size="sm" placeholder="Batch no." style={{ width: 150 }} value={bSearch} onChange={(e) => setBSearch(e.target.value)} />
              {statuses.length > 1 && (
                <Select allowClear size="small" placeholder="Any status" style={{ width: 140 }} value={bStatus} onChange={setBStatus}
                  options={statuses.map((st) => ({ value: st, label: st }))} />
              )}
              <Select size="small" style={{ width: 150 }} value={bExp} onChange={(v) => setBExp(v as any)}
                options={[{ value: 'any', label: 'Any expiry' }, { value: '30', label: 'Expiring ≤30d' }, { value: '90', label: 'Expiring ≤90d' }]} />
              <DatePicker.RangePicker size="small" style={{ width: 230 }} placeholder={['Mfg from', 'to']} allowEmpty={[true, true]}
                value={mfgRange as any} onChange={(r) => setMfgRange((r as any) ?? [null, null])} />
              <DatePicker.RangePicker size="small" style={{ width: 230 }} placeholder={['Expiry from', 'to']} allowEmpty={[true, true]}
                value={expRange as any} onChange={(r) => setExpRange((r as any) ?? [null, null])} />
              {batchFiltersOn && <Button variant="default" size="sm" onClick={resetBatchFilters}>Reset</Button>}
              <Select
                size="small" style={{ width: 96 }} value={bPageSize}
                onChange={(ps) => { setBPageSize(ps); setBPage(1); }}
                options={[8, 15, 30, 50].map((n) => ({ value: n, label: `${n} / page` }))}
              />
              <Pagination
                size="small" current={bCurrent} pageSize={bPageSize} total={rows.length} showSizeChanger={false}
                onChange={(p) => setBPage(p)} showTotal={(t, r) => `${r[0]}–${r[1]} of ${t}`}
              />
            </div>
          )}
        </div>
      )}

      {hiddenNonSellable > 0 && (
        <div className="mb-2 text-xs text-subtle">
          {hiddenNonSellable} non-sellable lot{hiddenNonSellable === 1 ? '' : 's'} (expired · QC-hold · blocked) hidden here — see the <span className="font-medium text-muted">Non-sellable</span> tab.
        </div>
      )}

      {rows.length ? (
        <div className="overflow-hidden rounded-control border border-line">
          <table className="w-full border-collapse text-sm">
            <thead className="bg-surface-2">
              <tr>
                <th className="w-8" />
                <th className={TH}>Batch</th>
                <th className={TH}>Mfg</th>
                <th className={TH}>Expiry</th>
                {!hideWh && <th className={`${TH} text-center`}>Warehouses</th>}
                <th className={`${TH} text-right`}>Qty</th>
                <th className={TH}>Status</th>
                {hasValue && <th className={`${TH} text-right`}>Value</th>}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((g) => {
                const d = daysUntil(g.expDate);
                const tint = g.statuses.has('EXPIRED') ? 'bg-danger-500/10' : d != null && d >= 0 && d <= 90 ? 'bg-warning-500/10' : '';
                const canExpand = !hideWh && g.splits.length > 1;
                const open = openBatches.has(g.batchNo);
                return (
                  <Fragment key={g.batchNo}>
                    <tr
                      className={`border-t border-line ${tint} ${canExpand ? 'cursor-pointer' : ''} ${canExpand && !tint ? 'hover:bg-surface-2' : ''}`}
                      onClick={canExpand ? () => toggleBatch(g.batchNo) : undefined}
                    >
                      <td className="px-2 align-middle">{canExpand && <Chevron open={open} />}</td>
                      <td className="px-3 py-2.5 font-mono text-xs text-content">
                        {g.batchNo}
                        {g.sourceBatchNo && <div className="mt-0.5 text-2xs font-normal text-subtle" title="Original batch this returned/quarantined stock came from">↩ from {g.sourceBatchNo}</div>}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-muted">{fmtDate(g.mfgDate)}</td>
                      <td className="px-3 py-2.5">{expiryCell(g.expDate)}</td>
                      {!hideWh && <td className="px-3 py-2.5 text-center">{g.splits.length === 1 ? <Pill>{g.splits[0].warehouse}</Pill> : <Pill color="blue">{g.splits.length} warehouses</Pill>}</td>}
                      <td className="px-3 py-2.5 text-right font-semibold text-content">{fmtPacks(g.qty)}</td>
                      <td className="px-3 py-2.5">{statusTag(g.status)}</td>
                      {hasValue && <td className="px-3 py-2.5 text-right text-content">{inr(g.value)}</td>}
                    </tr>
                    {canExpand && open && (
                      <tr className="bg-surface-2">
                        <td colSpan={99} className="p-0">
                          <div className="py-2 pl-10 pr-3">
                            <table className="w-full border-collapse text-sm">
                              <thead>
                                <tr>
                                  <th className={`${TH} py-1.5`}>Warehouse</th>
                                  <th className={`${TH} py-1.5 text-right`}>Qty</th>
                                  <th className={`${TH} py-1.5`}>Status</th>
                                  {hasValue && <th className={`${TH} py-1.5 text-right`}>Value</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {g.splits.map((s) => (
                                  <tr key={s.batchId} className="border-t border-line">
                                    <td className="px-3 py-1.5"><Pill>{s.warehouse}</Pill></td>
                                    <td className="px-3 py-1.5 text-right font-semibold text-content">{fmtPacks(s.qty)}</td>
                                    <td className="px-3 py-1.5"><Pill color={STATUS_PILL[effStatus(s)] ?? 'default'}>{effStatus(s)}</Pill></td>
                                    {hasValue && <td className="px-3 py-1.5 text-right text-content">{inr(s.value)}</td>}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : <EmptyState text="No batches match" />}
    </div>
  );
}

export function StockPage({ embedded }: { embedded?: boolean } = {}) {
  const { data, isLoading } = useQuery({
    queryKey: ['stock-catalog'],
    queryFn: () => request<Variant[]>({ url: '/catalog/with-stock' }),
  });

  const groups = useMemo(() => buildGroups(data ?? []), [data]);
  // A Warehouse Manager is scoped to one warehouse, so the warehouse columns just repeat the same
  // value on every row — hide them for that role. Admins / Super Admin (WAREHOUSE.VIEW) always keep
  // the warehouse breakdown, even when only one warehouse currently holds stock.
  const hideWarehouse = !usePermission('WAREHOUSE.VIEW');

  // Valuation figures (value, MRP, margin) folded into this table for users who may see them — shares
  // the same ['stock-valuation', {}] query as the valuation summary above it, so it's a single fetch.
  const canValuation = usePermission('STOCK.VALUATION.VIEW');
  const { data: valuation } = useQuery({
    queryKey: ['stock-valuation', {}],
    queryFn: () => request<any>({ url: '/stock/valuation' }),
    enabled: canValuation,
  });
  const valByProduct = useMemo(() => {
    const m = new Map<string, { costValue: number; retailValue: number; marginPct: number }>();
    for (const p of (valuation?.byProduct ?? [])) {
      m.set(String(p.productId), { costValue: Number(p.costValue), retailValue: Number(p.retailValue), marginPct: Number(p.marginPct) });
    }
    return m;
  }, [valuation]);
  // Per-(product, warehouse) value lens, so the figures stay correct when the page is scoped to one
  // warehouse; otherwise the combined `byProduct` figures above are used.
  const valByProductWh = useMemo(() => {
    const m = new Map<string, { costValue: number; retailValue: number; marginPct: number }>();
    for (const it of (valuation?.items ?? [])) {
      m.set(`${it.productId}-${it.warehouseId}`, { costValue: Number(it.costValue), retailValue: Number(it.retailValue), marginPct: Number(it.marginPct) });
    }
    return m;
  }, [valuation]);

  // Warehouse list → friendly picker labels + code→id for the per-warehouse value lookup.
  const { data: whList } = useQuery({
    queryKey: ['wh-list'],
    queryFn: () => request<any[]>({ url: '/warehouses' }),
    enabled: !hideWarehouse,
    staleTime: 5 * 60_000,
  });
  const whMeta = useMemo(() => {
    const byCode = new Map<string, { id: string; name: string }>();
    for (const w of whList ?? []) byCode.set(String(w.code), { id: String(w.id), name: String(w.name) });
    return byCode;
  }, [whList]);

  // --- Filters (all client-side over the fetched catalogue) ---
  const [search, setSearch] = useState('');
  const [whCode, setWhCode] = useState<string>();
  const [stockState, setStockState] = useState<'in' | 'out' | 'all'>('in');
  const [gPage, setGPage] = useState(1);
  const [gPageSize, setGPageSize] = useState(20);
  const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
  const [openVariants, setOpenVariants] = useState<Set<string>>(new Set());
  const toggleGroup = (k: string) => setOpenGroups((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const toggleVariant = (id: string) => setOpenVariants((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const filtersOn = !!(search || whCode || stockState !== 'in');

  const whId = whCode ? whMeta.get(whCode)?.id : undefined;
  const ZERO = { costValue: 0, retailValue: 0, marginPct: NaN };
  const valOf = (productId: string | number) =>
    (whId ? valByProductWh.get(`${productId}-${whId}`) : valByProduct.get(String(productId))) ?? ZERO;

  // Warehouse picker options = ALL warehouses (not only those currently holding stock — selecting an empty one
  // just shows no stock). (isMaster is retained on the option data; the gold master capsule that the antd
  // Select's optionRender used to draw is not rendered by the ui Select.)
  const whOptions = useMemo(() =>
    (whList ?? [])
      .filter((w: any) => w.code)
      .map((w: any) => ({ value: String(w.code), label: warehouseText(w), isMaster: !!w.isMaster }))
      .sort((a: any, b: any) => a.value.localeCompare(b.value)),
    [whList]);

  // Scope to a single warehouse (rebuild each variant's stock to just that warehouse), then search +
  // stock-state. Rebuilding the stock means the cell renders work unchanged when scoped.
  const view = useMemo(() => {
    let gs = groups;
    if (whCode) {
      gs = groups.map((g) => {
        const variants = g.variants.map((v) => {
          const q = v.stock.warehouses[whCode] ?? 0;
          return { ...v, stock: { warehouses: q > 0 ? { [whCode]: q } : {}, total: q } };
        });
        const total = variants.reduce((s, v) => s + v.stock.total, 0);
        return { ...g, variants, total, warehouseTotals: total > 0 ? { [whCode]: total } : {} };
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      gs = gs.filter((g) =>
        g.groupName.toLowerCase().includes(q) ||
        g.variants.some((v) => [v.sku, v.variantLabel, v.name].some((x) => String(x ?? '').toLowerCase().includes(q))));
    }
    if (stockState === 'in') gs = gs.filter((g) => g.total > 0);
    else if (stockState === 'out') gs = gs.filter((g) => g.total === 0);
    return gs;
  }, [groups, whCode, search, stockState]);

  // Warehouse tags with overflow — a product spread across many warehouses stays on one line:
  // show the first few, then "+N more" with the rest in a tooltip.
  const whTags = (entries: [string, number][], color?: string) => {
    if (!entries.length) return <span className="text-subtle">Out of stock</span>;
    const shown = entries.slice(0, 4);
    const rest = entries.slice(4);
    return (
      <span className="inline-flex flex-wrap gap-1">
        {shown.map(([wh, q]) => <Pill key={wh} color={color ?? 'default'}>{wh}: {fmt(q)}</Pill>)}
        {rest.length > 0 && (
          <Tooltip label={rest.map(([wh, q]) => `${wh}: ${fmt(q)}`).join(', ')}>
            <span className="inline-flex"><Pill className="cursor-default">+{rest.length} more</Pill></span>
          </Tooltip>
        )}
      </span>
    );
  };

  const colCount = 4 + (hideWarehouse ? 0 : 1) + (canValuation ? 1 : 0);
  const gPageCount = Math.max(1, Math.ceil(view.length / gPageSize));
  const gCurrent = Math.min(gPage, gPageCount);
  const pagedGroups = view.slice((gCurrent - 1) * gPageSize, gCurrent * gPageSize);
  const onFilterChange = () => setGPage(1);

  // Nested variant table for an expanded group.
  const renderVariants = (g: Group) => (
    <table className="w-full border-collapse text-sm">
      <thead>
        <tr>
          <th className="w-8" />
          <th className={TH}>Variant</th>
          <th className={TH}>SKU</th>
          <th className={TH}>Pack</th>
          {!hideWarehouse && <th className={TH}>Warehouses</th>}
          <th className={`${TH} text-right`}>Total</th>
          {canValuation && (
            <>
              <th className={`${TH} text-right`}>Value (dealer)</th>
              <th className={`${TH} text-right`}>Value (MRP)</th>
              <th className={`${TH} text-right`}>Margin %</th>
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {g.variants.map((v) => {
          const open = openVariants.has(v.productId);
          const val = valOf(v.productId);
          return (
            <Fragment key={v.productId}>
              <tr className="cursor-pointer border-t border-line hover:bg-surface-2" onClick={() => toggleVariant(v.productId)}>
                <td className="px-2 align-middle"><Chevron open={open} /></td>
                <td className="px-3 py-2.5"><Pill color="green">{v.variantLabel ?? v.sku}</Pill></td>
                <td className="px-3 py-2.5 text-muted">{v.sku}</td>
                <td className="px-3 py-2.5 text-subtle">{fmtUom(v.packSize, v.uom)}</td>
                {!hideWarehouse && <td className="px-3 py-2.5">{whTags(Object.entries(v.stock.warehouses ?? {}).filter(([, q]) => q > 0))}</td>}
                <td className="px-3 py-2.5 text-right font-semibold text-content">{fmtPacks(v.stock.total)}</td>
                {canValuation && (
                  <>
                    <td className="px-3 py-2.5 text-right text-content">{inr(val.costValue)}</td>
                    <td className="px-3 py-2.5 text-right text-content">{inr(val.retailValue)}</td>
                    <td className="px-3 py-2.5 text-right text-content">{val.marginPct != null && !Number.isNaN(val.marginPct) ? `${val.marginPct}%` : '—'}</td>
                  </>
                )}
              </tr>
              {open && (
                <tr>
                  <td colSpan={99} className="p-0">
                    <div className="pl-10 pr-2">
                      <BatchPanel productId={v.productId} hideWarehouse={hideWarehouse} warehouseCode={whCode} />
                    </div>
                  </td>
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div>
      {!embedded && <h2 className="mb-3 text-xl font-semibold text-content">Warehouse Stock</h2>}
      <div className="overflow-hidden rounded-card border border-line bg-surface shadow-soft">
        {/* Filter bar (Antd controls) */}
        <div className="flex flex-wrap items-center gap-2 p-3">
          <Input allowClear placeholder="Product or SKU" style={{ width: 220 }} value={search} onChange={(e) => { setSearch(e.target.value); onFilterChange(); }} />
          {!hideWarehouse && (
            <Select
              allowClear showSearch optionFilterProp="label" placeholder="All warehouses" style={{ width: 230 }}
              value={whCode} onChange={(v) => { setWhCode(v); onFilterChange(); }} options={whOptions}
            />
          )}
          <Segmented
            value={stockState} onChange={(v) => { setStockState(v as any); onFilterChange(); }}
            options={[{ label: 'In stock', value: 'in' }, { label: 'Out of stock', value: 'out' }, { label: 'All', value: 'all' }]}
          />
          {filtersOn && <Button onClick={() => { setSearch(''); setWhCode(undefined); setStockState('in'); onFilterChange(); }}>Reset</Button>}
        </div>

        {/* Table */}
        <div className="overflow-x-auto border-t border-line">
          {isLoading ? (
            <div className="space-y-2 p-4">
              {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-12 animate-pulse rounded-control bg-surface-3" />)}
            </div>
          ) : view.length === 0 ? (
            <EmptyState text={filtersOn ? 'No products match these filters' : 'No stock yet'} />
          ) : (
            <table className="w-full border-collapse text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <th className="w-8" />
                  <th className="w-16" />
                  <th className={TH}>Product</th>
                  {!hideWarehouse && <th className={TH}>Warehouses (combined)</th>}
                  <th className={`${TH} text-right`}>Total</th>
                  {canValuation && <th className={`${TH} text-right`}>Value (dealer)</th>}
                </tr>
              </thead>
              <tbody>
                {pagedGroups.map((g) => {
                  const open = openGroups.has(g.key);
                  const gValue = g.variants.reduce((s, v) => s + valOf(v.productId).costValue, 0);
                  return (
                    <Fragment key={g.key}>
                      <tr className="cursor-pointer border-t border-line hover:bg-surface-2" onClick={() => toggleGroup(g.key)}>
                        <td className="px-2 align-middle"><Chevron open={open} /></td>
                        <td className="py-3 pl-2 pr-1"><GroupAvatar url={g.imageUrl} name={g.groupName} /></td>
                        <td className="px-3 py-3">
                          <div className="font-semibold text-content">{g.groupName}</div>
                          <div className="text-xs text-subtle">
                            {g.variants.length} variant{g.variants.length > 1 ? 's' : ''} · {g.variants.map((v) => v.variantLabel ?? v.sku).join(' / ')}
                          </div>
                        </td>
                        {!hideWarehouse && <td className="px-3 py-3">{whTags(Object.entries(g.warehouseTotals).filter(([, q]) => q > 0), 'blue')}</td>}
                        <td className="px-3 py-3 text-right text-base font-bold text-content">{fmt(g.total)}</td>
                        {canValuation && <td className="px-3 py-3 text-right font-semibold text-brand-700 dark:text-brand-400">{inr(gValue)}</td>}
                      </tr>
                      {open && (
                        <tr className="bg-surface-2">
                          <td colSpan={colCount} className="p-0">
                            <div className="border-t border-line py-1 pl-12 pr-2">
                              {renderVariants(g)}
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {view.length > 0 && (
          <div className="flex items-center justify-end border-t border-line px-3 py-2.5">
            <Pagination
              size="small"
              current={gCurrent}
              pageSize={gPageSize}
              total={view.length}
              showSizeChanger
              pageSizeOptions={[10, 20, 50, 100]}
              onChange={(p, ps) => { setGPage(p); setGPageSize(ps); }}
              showTotal={(t) => `${t} products`}
            />
          </div>
        )}
      </div>
    </div>
  );
}
